import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/features/auth/AuthProvider';
import { canViewFeedbackHistory } from '@/utils/feedbackAccess';
import { Button } from '@/components/ui/button';
import { LoadingScreen } from '@/components/loading/LoadingScreen';

/**
 * Authorization for /feedback-history/:uid? — a privacy-sensitive route that
 * renders a user's coach ratings + free-text coach notes (some about named
 * children). Before the fix this route had NO authorization: any authenticated
 * caller could read any user's rating history by URL manipulation.
 *
 * The access decision lives in `canViewFeedbackHistory` (@/utils/feedbackAccess)
 * — the single source of truth used by BOTH this route-level guard (Layer 1) and
 * the component's own defensive read (Layer 2, in StudentFeedbackHistory.tsx).
 * The two-layer pattern means a future route-config mistake can't silently
 * re-open the hole — the component re-verifies before it ever issues a read.
 */

/**
 * The denial UI, shared by the guard and the component so both layers render
 * the identical "you don't have permission" page. Does NOT redirect — a silent
 * bounce would hide the attempt.
 */
export const FeedbackHistoryDenied: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-6 text-center">
            <ShieldAlert className="h-12 w-12 text-muted-foreground" />
            <h2 className="text-xl font-semibold">{t('feedback_history.access_denied_title')}</h2>
            <p className="text-muted-foreground max-w-md">{t('feedback_history.access_denied')}</p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
                {t('feedback_history.back_to_dashboard')}
            </Button>
        </div>
    );
};

/**
 * Layer 1 — route-level gate. Wrap the /feedback-history/:uid? route with this.
 *
 * The decision is stored WITH the uid it was made for, so a decision left over
 * from a previous uid never leaks the new target's content: if `decision.uid`
 * no longer matches the current `uid`, we fall back to the loading state and
 * re-check. (This also keeps the async result out of a synchronous
 * set-state-in-effect.)
 */
export const FeedbackHistoryGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const { uid } = useParams();
    const { currentUser } = useAuth();
    const [decision, setDecision] = useState<{ uid?: string; allowed: boolean } | null>(null);

    useEffect(() => {
        let cancelled = false;
        if (!currentUser) return; // unauthenticated → redirect handled below
        canViewFeedbackHistory(currentUser, uid).then((allowed) => {
            if (!cancelled) setDecision({ uid, allowed });
        });
        return () => {
            cancelled = true;
        };
    }, [uid, currentUser]);

    if (!currentUser) return <Navigate to="/login" replace />;
    // No decision yet, or a decision for a now-stale uid → keep checking.
    if (!decision || decision.uid !== uid) {
        return <LoadingScreen message={t('feedback_history.loading')} />;
    }
    if (!decision.allowed) return <FeedbackHistoryDenied />;
    return <>{children}</>;
};
